import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import { marked } from 'marked';
import {
  Answer,
  Question,
  QuestionInPage,
  QuestionnaireAnswer,
  QuestionType,
} from 'src/app/core/models/questionnaire.model';
import { QuestionContext } from '../question-multi-file/question-multi-file.component';
import { QuestionTooltipComponent } from '../question-tooltip/question-tooltip.component';
import { EligibilityInfo } from '../../smart/questionnaire-answer/core/eligibility.service';
import { SpeechSynthesisService } from 'src/app/core/services/speech-synthesis.service';
import { resolveStatementShortcodes } from 'src/app/core/utils/statement-shortcodes.util';

@Component({
  selector: 'app-question',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss'],
})
export class QuestionComponent implements OnInit, OnChanges {
  @Input('context') context!: QuestionContext;
  @Input() isHidden = false;
  @Input('question') question!: QuestionInPage;
  @Input('questionnaireAnswer') qa$!: Observable<QuestionnaireAnswer | null>;
  @Input() ineligibilityInfo!: EligibilityInfo;
  @Input() user: any;

  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  firstChange = true;
  firstAnswer: Answer | null = null;
  tooltipText = '';
  tooltipConfig: any = null;
  parsedStatement: SafeHtml;

  // Speech synthesis (CPK-3982) — leitura em voz alta da pergunta
  /** texto puro da pergunta, usado na síntese de voz */
  private spokenText = '';
  /** emite true quando ESTA pergunta está sendo lida no momento */
  isSpeaking$: Observable<boolean>;

  // Ineligibility warning
  showIneligibilityWarning = false;
  ineligibilityMessage = '';

  constructor(
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    public speech: SpeechSynthesisService,
  ) {
    this.parsedStatement = this.sanitizer.bypassSecurityTrustHtml('');
    this.isSpeaking$ = this.speech.speaking$.pipe(
      map((id) => !!id && id === this.question?.cp_question?.id),
    );
  }

  private parseStatement(): void {
    // CPK-4758: shortcodes {{ cp_pii.* }} resolvidos com o PII do dono da claim
    const raw = resolveStatementShortcodes(this.question?.cp_question?.statement, this.user?.pii);
    const html = marked.parse(raw, { gfm: true, breaks: true }) as string;
    // bypassSecurityTrustHtml: statement is authored by internal admins only
    this.parsedStatement = this.sanitizer.bypassSecurityTrustHtml(html);
    this.spokenText = this.buildSpokenText(html);
  }

  /**
   * Deriva o texto puro para a leitura em voz alta a partir do HTML já
   * renderizado, para o áudio corresponder ao enunciado exibido, porém sem
   * ruídos que confundem o TTS nativo:
   *  - remove as tags HTML geradas pelo marked;
   *  - remove emojis / pictogramas (ex.: ✏️ seria lido como "lápis");
   *  - remove a numeração do início do enunciado (ex.: "1.01.01 - "), que
   *    o TTS interpreta como data ("primeiro de janeiro de 2001");
   *  - normaliza espaços. Pontuação (inclusive "?") é preservada.
   */
  private buildSpokenText(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || '';

    // Emojis, pictogramas, seletores de variação e ZWJ.
    text = text.replace(/[\p{Extended_Pictographic}‍️]/gu, '');

    // Numeração inicial ("1", "1.2", "1.01.06") seguida de separador
    // ( - – — ) : ). Números legítimos no meio da frase são preservados,
    // pois exigimos o separador logo após o grupo numérico inicial.
    text = text.replace(/^\s*\d+(?:[.\-]\d+)*\s*[-–—).:]\s+/u, '');

    return text.replace(/\s+/g, ' ').trim();
  }

  /** True quando há texto para ler e o browser suporta a síntese de voz. */
  get canSpeak(): boolean {
    return this.speech.isSupported && !!this.spokenText;
  }

  /**
   * Alterna a leitura em voz alta desta pergunta: se já estiver tocando,
   * interrompe; caso contrário, lê o texto no idioma atual do app.
   */
  toggleSpeak(): void {
    const id = this.question?.cp_question?.id;
    if (!id) {
      return;
    }
    if (this.speech.isSpeakingId(id)) {
      this.speech.stop();
      return;
    }
    this.speech.speak(id, this.spokenText, this.speechLang());
  }

  /** Mapeia o idioma atual do app para um locale BCP-47 de voz. */
  private speechLang(): string {
    const lang = (this.translate.currentLang || this.translate.defaultLang || '').toLowerCase();
    return lang.startsWith('pt') ? 'pt-BR' : 'en-US';
  }

  ngOnInit(): void {
    if (this.question?.cp_question && !this.question.cp_question.answer) {
      this.question.cp_question.answer = {
        value: null,
        isIgnored: false
      };
    }

    this.firstAnswer = this.question?.cp_question?.answer || null;
    this.computeTooltipText();
    this.parseStatement();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question']) {
      if (this.question?.cp_question && !this.question.cp_question.answer) {
        this.question.cp_question.answer = {
          value: null,
          isIgnored: false
        };
      }

      this.computeTooltipText();
      this.parseStatement();
    } else if (changes['user'] && !changes['user'].firstChange) {
      this.parseStatement();
    }

    if (changes['ineligibilityInfo']) {
      this.checkIneligibility();
    }
  }

  private computeTooltipText() {
    const tooltip = this.question?.cp_question?.configuration?.tooltip;
    this.tooltipConfig = tooltip || null;
    if (!tooltip) {
      this.tooltipText = '';
      return;
    }

    const lines: string[] = [];

    if (tooltip.user?.text) {
      lines.push(tooltip.user.text);
    }

    lines.push('');
    // lines.push('Click to watch the video!');

    this.tooltipText = lines.join('\n');
  }

  change(ev: QuestionAnswer) {
    // firstChange must be consumed on the very first call regardless of outcome —
    // leaving it true whenever the first real edit's value differs from firstAnswer
    // means a LATER edit that happens to revert back to the original value (e.g.
    // draw a signature, then clear it back to blank) gets silently swallowed here,
    // never reaching the parent. That desynced the parent's answer from what the
    // canvas actually showed (CPK-4390).
    const isFirstChange = this.firstChange;
    this.firstChange = false;

    if (isFirstChange && ev.answer.value === this.firstAnswer?.value) {
      return;
    }

    this.changeAnswer.next(ev);

    // Check inelegibilidade após mudança
    this.checkIneligibility();
  }

  /**
   * Verifica se esta questão está causando inelegibilidade da claim
   */
  private checkIneligibility(): void {
    if (!this.ineligibilityInfo || !this.ineligibilityInfo.isIneligible) {
      this.showIneligibilityWarning = false;
      this.ineligibilityMessage = '';
      return;
    }

    const questionSlug = this.question?.cp_question?.slug;
    if (!questionSlug) {
      this.showIneligibilityWarning = false;
      return;
    }

    // Verifica se esta questão específica está afetada
    if (!this.ineligibilityInfo.affectedQuestions.has(questionSlug)) {
      this.showIneligibilityWarning = false;
      this.ineligibilityMessage = '';
      return;
    }

    // Buscar mensagem de erro mais específica para esta questão
    // Prioridade: errorMessage da condição individual > errorMessage da regra > mensagem genérica
    let customMessage: string | null = null;
    let shouldHideMessage = false;

    for (const violation of this.ineligibilityInfo.violatedRules) {
      for (const cond of violation.matchedConditions) {
        const keyParts = cond.key.split('.');
        if (keyParts[0] === 'answers' && keyParts[1] === questionSlug) {
          // Verificar se esta condição tem hideErrorMessage = true
          if ((cond as any).hideErrorMessage === true) {
            shouldHideMessage = true;
            break;
          }
          customMessage = cond.errorMessage || violation.errorMessage;
          if (customMessage) break;
        }
      }
      if (customMessage || shouldHideMessage) break;
    }

    // Se hideErrorMessage está ativo, não mostrar warning
    if (shouldHideMessage) {
      this.showIneligibilityWarning = false;
      this.ineligibilityMessage = '';
      return;
    }

    this.showIneligibilityWarning = true;
    this.ineligibilityMessage = customMessage ||
      'This answer may affect your eligibility for this claim.';
  }

  showTooltip() {
    const tooltip = this.tooltipConfig || this.question.cp_question.configuration?.tooltip;
    if (!tooltip) return;

    const userTip = tooltip.user;
    if (!userTip) return;

    this.dialog.open(QuestionTooltipComponent, {
      data: { text: userTip.text || '', youtube_video: userTip.youtube_video || null },
      width: '90vw',
      maxWidth: '760px',
      panelClass: 'question-tooltip-panel',
    });
  }
}

export interface QuestionAnswer {
  question: Question;
  answer: Answer;
}
